import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApaRangesComponent as ParentApaRangesComponent } from '@shared-lib/global-components/apa-ranges/apa-ranges.component';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { environment } from '../../../projects/my-app/src/environments/environment';

@Component({
  selector: 'app-apa-ranges',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './apa-ranges.component.html',
  styleUrls: ['./apa-ranges.component.scss'],
})
export class ApaRangesComponent extends ParentApaRangesComponent {
  submitState: 'idle' | 'submitting' | 'success' | 'error' = 'idle';
  submitMessage = '';
  private lastBoundaryChangedRow: any = null;
  private readonly customGlobalFilterService: GlobalFilterService;

  constructor(
    private readonly customHttp: HttpClient,
    globalFilterService: GlobalFilterService,
  ) {
    super(customHttp, globalFilterService);
    this.customGlobalFilterService = globalFilterService;
  }

  ngDoCheck(): void {
    this.normalizeApiRangeRows(this.rows);
    this.normalizeApiRangeRows((this as any).allRows);
    this.applySelectedApiRangeFilter();
    this.applyConfiguredRangeOrder();
    this.initializeConfiguredBounds();
  }

  override onSlide(row: any, event: Event): void {
    if (!this.isRangeEditable) {
      return;
    }

    super.onSlide(row, event);
  }

  override onBoundaryChange(row: any, key: 'min' | 'max'): void {
    if (!this.isRangeEditable) {
      return;
    }

    this.clampBoundary(row, key);
    super.onBoundaryChange(row, key);
    this.clampBoundary(row, key);
    this.lastBoundaryChangedRow = row;
  }

  onBoundaryInput(row: any, key: 'min' | 'max', event: Event): void {
    if (!this.isRangeEditable) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const rawValue = input.value.trim();

    if (rawValue === '' || rawValue === '-' || rawValue === '.' || rawValue === '-.') {
      return;
    }

    const value = this.clampBoundaryValue(row, key, Number(rawValue), Number(row?.[key]));
    if (!Number.isFinite(value)) {
      return;
    }

    row[key] = value;
    if (value !== Number(rawValue)) {
      input.value = String(value);
    }
  }

  getMinBoundaryInputMin(): number | null {
    return this.resolveConfiguredBoundary('min');
  }

  getMinBoundaryInputMax(row: any): number | null {
    const rowMax = Number(row?.max);
    return Number.isFinite(rowMax) ? rowMax : this.resolveConfiguredBoundary('max');
  }

  getMaxBoundaryInputMin(row: any): number | null {
    const rowMin = Number(row?.min);
    return Number.isFinite(rowMin) ? rowMin : this.resolveConfiguredBoundary('min');
  }

  getMaxBoundaryInputMax(): number | null {
    return this.resolveConfiguredBoundary('max');
  }

  get showSubmitButton(): boolean {
    return this.userInputEnabled && (this.data?.submitAction?.enabled === true || this.data?.showSubmitButton === true);
  }

  get isRangeEditable(): boolean {
    return this.userInputEnabled
      && this.data?.editable !== false
      && this.data?.enableRangeEditing !== false
      && this.data?.readOnly !== true
      && this.data?.readonly !== true;
  }

  get submitButtonLabel(): string {
    return String(
      this.data?.submitAction?.label ??
      this.data?.submitButtonLabel ??
      'Submit',
    );
  }

  get isSubmitting(): boolean {
    return this.submitState === 'submitting';
  }

  submitRanges(): void {
    if (!this.userInputEnabled) {
      return;
    }

    const submitAction = this.data?.submitAction || {};
    const apiUrl = String(submitAction.apiUrl || this.data?.submitApiUrl || '').trim();

    if (!apiUrl) {
      this.submitState = 'error';
      this.submitMessage = 'Submit URL is not configured.';
      return;
    }

    this.submitState = 'submitting';
    this.submitMessage = '';

    const method = String(submitAction.method || 'post').toLowerCase();
    const payload = this.buildSubmitPayload(submitAction);
    if (!payload) {
      this.submitState = 'error';
      this.submitMessage = String(submitAction.noChangesMessage || 'Update a range before submitting.');
      return;
    }

    const options = {
      body: payload,
      headers: submitAction.headers || this.data?.submitHeaders || undefined,
    };

    this.customHttp.request(method, this.resolveSubmitUrl(apiUrl), options).subscribe({
      next: () => {
        this.submitState = 'success';
        this.submitMessage = String(submitAction.successMessage || 'Saved.');
      },
      error: () => {
        this.submitState = 'error';
        this.submitMessage = String(submitAction.errorMessage || 'Unable to submit APA ranges.');
      },
    });
  }

  private resolveSubmitUrl(url: string): string {
    const isLocalAssets = url.startsWith('assets') || url.startsWith('/assets');
    const isAbsoluteUrl = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);

    if (isLocalAssets || isAbsoluteUrl) {
      return url;
    }

    const baseUrl = environment.URL.endsWith('/')
      ? environment.URL.slice(0, -1)
      : environment.URL;
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${path}`;
  }

  private get userInputEnabled(): boolean {
    return this.resolveBooleanFlag((environment as any).USER_INPUT_ENABLED) ?? true;
  }

  private resolveBooleanFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
        return true;
      }
      if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
        return false;
      }
    }

    return null;
  }

  private buildSubmitPayload(submitAction: any): any {
    if (submitAction.boundaryUpdate === true || submitAction.payloadType === 'boundary-update') {
      return this.buildBoundaryUpdatePayload(submitAction);
    }

    const requestData = {
      ...(submitAction.requestData || submitAction.defaultRequestData || {}),
      ...this.resolveRangeRequestData(submitAction),
    };

    const payloadTemplate = submitAction.payload || {};
    const payload = {
      responseList: [],
      entity: 'apa_ranges',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [],
      version: '1.0',
      ...payloadTemplate,
    };

    return {
      ...payload,
      requestData: {
        ...(payloadTemplate.requestData || {}),
        ...requestData,
      },
    };
  }

  private buildBoundaryUpdatePayload(submitAction: any): any | null {
    const row = this.lastBoundaryChangedRow;
    const productPriceType = String(row?.product_price_type ?? row?.productPriceType ?? row?.label ?? '').trim();
    const lowerLimit = Number(row?.min);
    const upperLimit = Number(row?.max);

    if (!productPriceType || !Number.isFinite(lowerLimit) || !Number.isFinite(upperLimit)) {
      return null;
    }

    const payloadTemplate = submitAction.payload || {};
    return {
      responseList: [],
      entity: 'mines',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName: 'ApaRangeUpdate',
          version: '1.0',
        },
      ],
      communicationType: 'rest',
      version: '1.0',
      ...payloadTemplate,
      requestData: {
        ...(payloadTemplate.requestData || {}),
        ...(submitAction.requestData || submitAction.defaultRequestData || {}),
        uuid: submitAction.requestData?.uuid ?? submitAction.defaultRequestData?.uuid ?? null,
        product_price_type: [productPriceType],
        lower_limit: lowerLimit,
        upper_limit: upperLimit,
      },
    };
  }

  private resolveRangeRequestData(submitAction: any): Record<string, number> {
    const keyMap = submitAction.rangeKeyMap || submitAction.valueKeyMap || {
      'Strategic/Target': 'strategic',
      'MFN/Star': 'mfn',
      'Key Accounts': 'key_accounts',
      'Review/Others': 'review',
    };
    const result: Record<string, number> = {};

    for (const row of this.rows || []) {
      const outputKey = this.resolveOutputKey(row?.label, keyMap);
      if (!outputKey) {
        continue;
      }

      const value = Number(this.resolveSubmitValue(row, submitAction));
      if (Number.isFinite(value)) {
        result[outputKey] = value;
      }
    }

    for (const row of this.rows || []) {
      const outputKey = this.resolveOutputKey(row?.label, keyMap);
      if (!outputKey) {
        continue;
      }

      const lowerLimitKey = this.resolveLowerLimitOutputKey(outputKey, row?.label, submitAction);
      const lowerLimitValue = Number(row?.min);
      if (lowerLimitKey && Number.isFinite(lowerLimitValue)) {
        result[lowerLimitKey] = lowerLimitValue;
      }
    }

    return result;
  }

  private resolveLowerLimitOutputKey(outputKey: string, label: any, submitAction: any): string {
    const lowerLimitKeyMap = submitAction.lowerLimitKeyMap || submitAction.lowerlimitKeyMap || {};
    const mappedKey = this.resolveOutputKey(label, lowerLimitKeyMap);
    return mappedKey || `lowerlimit_${outputKey}`;
  }

  private resolveSubmitValue(row: any, submitAction: any): any {
    const valueKey = String(
      submitAction.submitValueKey ||
      submitAction.rangeValueKey ||
      submitAction.valueSource ||
      'currentValue',
    ).trim();

    if (valueKey && row?.[valueKey] !== undefined && row?.[valueKey] !== null && row?.[valueKey] !== '') {
      return row[valueKey];
    }

    return row?.currentValue ?? row?.value;
  }

  private resolveOutputKey(label: any, keyMap: Record<string, string>): string {
    const normalizedLabel = this.normalizeLookup(label);
    const matchedEntry = Object.entries(keyMap || {}).find(([sourceLabel]) => (
      this.normalizeLookup(sourceLabel) === normalizedLabel
    ));

    return String(matchedEntry?.[1] || '').trim();
  }

  private normalizeLookup(value: any): string {
    return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private initializeConfiguredBounds(): void {
    const min = this.resolveConfiguredBoundary('min');
    const max = this.resolveConfiguredBoundary('max');

    if (min === null && max === null) {
      return;
    }

    this.initializeConfiguredBoundsForRows(this.rows, min, max);
    this.initializeConfiguredBoundsForRows((this as any).allRows, min, max);
  }

  private initializeConfiguredBoundsForRows(rows: any, configuredMin: number | null, configuredMax: number | null): void {
    if (!Array.isArray(rows)) {
      return;
    }

    for (const row of rows) {
      if (row?.__dvmApaBoundsInitialized === true) {
        continue;
      }

      const rowMin = Number(row?.min);
      const rowMax = Number(row?.max);
      const min = Number.isFinite(rowMin) ? rowMin : 0;
      const max = Number.isFinite(rowMax) ? rowMax : min;
      const safeMin = configuredMin !== null ? Math.max(min, configuredMin) : min;
      const safeMax = configuredMax !== null ? Math.min(max, configuredMax) : max;
      const effectiveMin = safeMin;
      const effectiveMax = safeMax >= safeMin ? safeMax : safeMin;

      if (!row || !Number.isFinite(effectiveMin) || !Number.isFinite(effectiveMax)) {
        continue;
      }

      row.min = effectiveMin;
      row.max = effectiveMax;
      row.currentValue = this.clampToConfiguredRange(Number(row.currentValue ?? row.value), effectiveMin, effectiveMax);
      Object.defineProperty(row, '__dvmApaBoundsInitialized', {
        value: true,
        configurable: true,
      });
    }
  }

  private normalizeApiRangeRows(rows: any): void {
    if (!Array.isArray(rows)) {
      return;
    }

    const configuredMin = this.resolveConfiguredBoundary('min');
    const configuredMax = this.resolveConfiguredBoundary('max');

    for (const row of rows) {
      if (!row || row.__dvmApaApiRowsNormalized === true) {
        continue;
      }

      const lowerLimit = this.firstFiniteNumber(row.lower_limit, row.lowerLimit, row.min);
      const upperLimit = this.firstFiniteNumber(row.upper_limit, row.upperLimit, row.max);
      const sliderValue = this.firstFiniteNumber(row.slider_value, row.sliderValue, row.currentValue, row.value);
      const label = row.label ?? row.product_price_type ?? row.productPriceType;

      if (!label || lowerLimit === null || upperLimit === null) {
        continue;
      }

      row.label = String(label);
      row.min = configuredMin !== null ? Math.max(lowerLimit, configuredMin) : lowerLimit;
      row.max = configuredMax !== null ? Math.min(upperLimit, configuredMax) : upperLimit;
      if (row.min > row.max) {
        row.min = row.max;
      }
      row.value = sliderValue ?? lowerLimit;
      row.currentValue = this.clampToConfiguredRange(row.value, lowerLimit, upperLimit);
      row.currentValue = this.clampToConfiguredRange(row.currentValue, row.min, row.max);

      Object.defineProperty(row, '__dvmApaApiRowsNormalized', {
        value: true,
        configurable: true,
      });
    }
  }

  private firstFiniteNumber(...values: any[]): number | null {
    for (const value of values) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        return numericValue;
      }
    }

    return null;
  }

  private applySelectedApiRangeFilter(): void {
    if (this.data?.showSelectedProductPriceTypeOnly !== true) {
      return;
    }

    const allRows = (this as any).allRows;
    if (!Array.isArray(allRows) || !allRows.length) {
      return;
    }

    const selected = this.resolveSelectedProductPriceType();
    if (!selected) {
      this.rows = [...allRows];
      return;
    }

    const normalizedSelected = this.normalizeLookup(selected);
    const filteredRows = allRows.filter((row: any) => {
      const rowLabel = row?.label ?? row?.product_price_type ?? row?.productPriceType;
      return this.normalizeLookup(rowLabel) === normalizedSelected;
    });

    this.rows = filteredRows.length ? filteredRows : [...allRows];
  }

  private applyConfiguredRangeOrder(): void {
    const order = this.resolveConfiguredRangeOrder();
    if (!order.length) {
      return;
    }

    this.sortRowsByConfiguredOrder(this.rows, order);
    this.sortRowsByConfiguredOrder((this as any).allRows, order);
  }

  private resolveConfiguredRangeOrder(): string[] {
    const configured =
      this.data?.rangeOrder ??
      this.data?.rowOrder ??
      this.data?.sliderOrder ??
      this.data?.order;

    if (!Array.isArray(configured)) {
      return [];
    }

    return configured
      .map((item: any) => this.normalizeLookup(item))
      .filter((item: string) => !!item);
  }

  private sortRowsByConfiguredOrder(rows: any, order: string[]): void {
    if (!Array.isArray(rows) || !rows.length) {
      return;
    }

    rows.sort((left: any, right: any) => {
      const leftIndex = this.getConfiguredOrderIndex(left, order);
      const rightIndex = this.getConfiguredOrderIndex(right, order);
      return leftIndex - rightIndex;
    });
  }

  private getConfiguredOrderIndex(row: any, order: string[]): number {
    const label = row?.label ?? row?.product_price_type ?? row?.productPriceType;
    const index = order.indexOf(this.normalizeLookup(label));
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  }

  private resolveSelectedProductPriceType(): any {
    const filters = this.customGlobalFilterService.getCurrentFilters();
    const requestParams = filters?.requestParams || {};
    const queryParams = filters?.queryParams || {};
    const selectedRaw =
      requestParams.product_price_type ??
      queryParams.product_price_type ??
      requestParams.selected_product_price_type ??
      queryParams.selected_product_price_type;

    return Array.isArray(selectedRaw) ? selectedRaw[0] : selectedRaw;
  }

  private clampBoundary(row: any, key: 'min' | 'max'): void {
    if (!row) {
      return;
    }

    const value = this.clampBoundaryValue(row, key, Number(row[key]), Number(row[key]));
    if (Number.isFinite(value)) {
      row[key] = value;
    }
  }

  private resolveConfiguredBoundary(key: 'min' | 'max'): number | null {
    const value =
      this.data?.sliderBounds?.[key] ??
      this.data?.rangeBounds?.[key] ??
      this.data?.[key === 'min' ? 'sliderMin' : 'sliderMax'];
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private clampToConfiguredRange(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) {
      return min;
    }

    return Math.max(min, Math.min(max, value));
  }

  private clampBoundaryValue(row: any, key: 'min' | 'max', value: number, fallbackValue: number): number {
    if (!Number.isFinite(value)) {
      return fallbackValue;
    }

    const lowerLimit = this.resolveConfiguredBoundary('min');
    const upperLimit = this.resolveConfiguredBoundary('max');
    const rowMin = Number(row?.min);
    const rowMax = Number(row?.max);
    const min = key === 'max' && Number.isFinite(rowMin) ? rowMin : lowerLimit ?? value;
    const max = key === 'min' && Number.isFinite(rowMax) ? rowMax : upperLimit ?? value;

    return this.clampToConfiguredRange(value, min, max);
  }
}
